import classNames from 'classnames'
import React, { useEffect, useRef, useState, useCallback } from 'react'
import styles from './autocomplete.module.css'

interface IOption {
  label: string
  value: string
  key?: string | number
}
/**
 * props need to pass from Consumer Component
 */
interface AutocompleteProps {
  name?: string
  label?: string
  required?: boolean
  options: IOption[]
  defaultValue?: string | number
  value?: string | number
  onChange?: (e: any) => void
  onSelect?: (e: any) => void
  disabled?: boolean
  isInvalid?: boolean
  isInvalidText?: string
  size?: 'md' | 'lg'
}

interface IAutoCompleteState {
  canShowOptions?: boolean
  searchValue?: string
  isSearching?: boolean
  selectedOption?: IOption
  cursor?: number
}

const autocompleteState: IAutoCompleteState = {
  canShowOptions: false,
  searchValue: '',
  isSearching: false,
  selectedOption: undefined,
  cursor: -1
}

const getSize = (size: string | undefined) => {
  switch (size) {
    case 'md':
      return ''
    case 'lg':
      return 'h-14'
    default:
      return ''
  }
}

export function Autocomplete(autocompleteProps: AutocompleteProps) {
  const {
    options,
    defaultValue,
    value,
    onChange,
    onSelect,
    label,
    required,
    disabled,
    isInvalid,
    isInvalidText,
    size
  }: AutocompleteProps = autocompleteProps
  const [state, setState] = useState<IAutoCompleteState>(autocompleteState)

  const {
    selectedOption,
    canShowOptions,
    searchValue = '',
    isSearching,
    cursor = -1
  }: IAutoCompleteState = state

  const updateComponentState = useCallback(
    (updatedState: IAutoCompleteState) => {
      setState((prevState: IAutoCompleteState) => {
        return {
          ...prevState,
          ...updatedState
        }
      })
    },
    [state]
  )

  const autoContainer = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let currentSelectedOption
    if (options.length) {
      currentSelectedOption = options.find((option: IOption): boolean => {
        return option.value === value || option.value === defaultValue
      })
    }
    updateComponentState({
      searchValue: currentSelectedOption?.label,
      selectedOption: currentSelectedOption
    })
  }, [defaultValue, value])

  const onOptionSelectClick = (option: IOption) => {
    onSelect && onSelect(option?.value)
    updateComponentState({
      canShowOptions: false,
      searchValue: option.label,
      selectedOption: option,
      isSearching: false,
      cursor: -1
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchText: string = e.target.value
    onChange && onChange(searchText)
    updateComponentState({
      searchValue: searchText,
      isSearching: true
    })
    if (!canShowOptions) {
      updateComponentState({ canShowOptions: true })
    }
  }

  let filteredOptions: IOption[] = options
  if (isSearching) {
    const regExMatchPattern = new RegExp('^' + searchValue, 'i')
    filteredOptions = options.filter((option: IOption): boolean => {
      return !!option.label.match(regExMatchPattern)
    })
  }

  useEffect(() => {
    const listener = (e: any) => {
      // native event
      if (
        autoContainer.current !== null &&
        !autoContainer.current.contains(e.target)
      ) {
        updateComponentState({ canShowOptions: false, isSearching: false })
      }
    }

    document.addEventListener('click', listener)
    document.addEventListener('focusin', listener)
    return () => {
      document.removeEventListener('click', listener)
      document.removeEventListener('focusin', listener)
    }
  }, [])

  const handleKeyDown = (evt: any) => {
    // arrow up/down button should select next/previous list element
    if (evt.key.toLowerCase() === 'arrowup') {
      // ArrowUp
      let currentCursorPosition = filteredOptions.length
      if (cursor && cursor > 0) {
        currentCursorPosition = cursor - 1
      }
      updateComponentState({
        cursor: currentCursorPosition
      })
    } else if (evt.key.toLowerCase() === 'arrowdown') {
      // ArrowDown
      let currentCursorPosition = 0
      if (cursor < filteredOptions.length - 1) {
        currentCursorPosition = cursor + 1
      }
      updateComponentState({
        cursor: currentCursorPosition
      })
    } else if (evt.key.toLowerCase() === 'enter') {
      // Enter
      if (cursor >= 0 && cursor <= filteredOptions.length) {
        onOptionSelectClick(filteredOptions[cursor])
      }
    }
  }

  const handleBlur = () => {
    if (isSearching) {
      if (filteredOptions.length === 0 || searchValue === '') {
        selectedOption && onOptionSelectClick(selectedOption)
      } else {
        onOptionSelectClick(filteredOptions[0])
      }
    }
  }

  return (
    <div className='relative block' ref={autoContainer}>
      <label className='block text-sm text-gray-500 mb-1'>
        {label} {required ? '*' : ''}
      </label>
      <input
        type='text'
        className={classNames({
          [styles.select]: true,
          [getSize(size)]: true,
          'appearance-none sm:text-sm bg-gray-100 block w-full pl-4 pr-10 py-2.5 text-base border focus:bg-white rounded focus:outline-none focus:ring-1':
            true,
          'focus:ring-danger focus:border-danger border-danger': isInvalid,
          'border-gray-300 focus:ring-primary focus:border-primary': !isInvalid,
          'cursor-not-allowed text-grey': disabled,
          [styles.selectDisabled]: disabled
        })}
        value={searchValue}
        onChange={handleChange}
        onFocus={() => updateComponentState({ canShowOptions: true })}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        onBlur={handleBlur}
      />

      <ul
        className={`max-h-80 flex-grow overflow-y-scroll absolute w-full z-10 py-1 rounded mt-1 shadow-lg bg-white border ${
          !canShowOptions && 'hidden'
        } select-none`}
      >
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option, intKey) => {
            let activeClass = ''
            if (option?.value === selectedOption?.value || cursor === intKey) {
              activeClass = 'bg-primary text-white'
            }
            const className = `px-4 py-2 text-sm hover:bg-primary hover:text-white ${activeClass}`
            return (
              <li
                className={className}
                key={option.key}
                onMouseDown={() => onOptionSelectClick(option)}
              >
                {option.label}
              </li>
            )
          })
        ) : (
          <li className='px-4 py-2 text-sm text-gray-500'>No results</li>
        )}
      </ul>
      {isInvalid ? (
        <div className='block text-xs text-danger-dark mt-1'>
          {isInvalidText}
        </div>
      ) : (
        ``
      )}
    </div>
  )
}
